import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCrown, FaUsers, FaFileInvoiceDollar, FaCreditCard, FaStickyNote, FaReceipt, FaFileAlt } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';

import defaultLogo from '../../../assets/images/wyenfos.png';
import './Sidebar.css';
import axios from 'axios';
import { getLogoUrl } from './utils/companyHelpers.js';
import { API_CONFIG } from '../../../utils/apiConfig';

const Sidebar = ({ isOpen, toggleSidebar, onCompanySelect, selectedCompany, refreshTrigger }) => {
  const closeTimerRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const { currentUser, userProfile } = useAuth();
  
  // Use Firebase user data instead of localStorage
  const user = userProfile || { role: 'admin', name: 'User' };
  
  // Ensure user object has all required properties
  const safeUser = {
    role: user?.role || 'admin',
    name: user?.name || 'User',
    ...user
  };

  const fetchCompanyNames = useCallback(async () => {
    if (!currentUser) return;

    setLoadingCompanies(true);
    
    // Use hardcoded companies since API endpoint doesn't exist
    const fallbackCompanies = [
      { _id: '1', name: 'WYENFOS INFOTECH', logoUrl: null, prefix: 'WIT' },
      { _id: '2', name: 'WYENFOS GOLD AND DIAMONDS', logoUrl: null, prefix: 'WGD' },
      { _id: '3', name: 'WYENFOS ADS', logoUrl: null, prefix: 'WAD' },
      { _id: '4', name: 'WYENFOS CASH VAPASE', logoUrl: null, prefix: 'WCV' },
      { _id: '5', name: 'AYUR4LIFE HERBALS INDIA', logoUrl: null, prefix: 'ALH' },
      { _id: '6', name: 'WYENFOS', logoUrl: null, prefix: 'WNF' },
      { _id: '7', name: 'WYENFOS PURE DROPS', logoUrl: null, prefix: 'WPD' },
      { _id: '8', name: 'WYENFOS CHARITABLE TRUST', logoUrl: null, prefix: 'WCT' }
    ];
    
    setCompanies(fallbackCompanies);
    
    if (!selectedCompany && fallbackCompanies.length > 0) {
      onCompanySelect(fallbackCompanies[0]);
    }

    setLoadingCompanies(false);
  }, [currentUser, selectedCompany, onCompanySelect]);

  useEffect(() => {
    if (currentUser) {
      fetchCompanyNames();
    } else {
      setLoadingCompanies(false);
    }
  }, [currentUser, fetchCompanyNames]);

  // Refresh companies when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && currentUser) {
      fetchCompanyNames();
    }
  }, [refreshTrigger, currentUser, fetchCompanyNames]);

  // Listen for localStorage changes to refresh companies
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'companies_refresh_trigger' && currentUser) {
        fetchCompanyNames();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUser, fetchCompanyNames]);

  // Separate useEffect to handle initial company selection when companies change
  useEffect(() => {
    if (!selectedCompany && companies.length > 0) {
      onCompanySelect(companies[0]);
    }
  }, [companies, selectedCompany, onCompanySelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-company-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    console.log('🔍 Sidebar: Toggle dropdown clicked, current state:', dropdownOpen);
    setDropdownOpen((prev) => !prev);
  };
  const closeDropdown = () => {
    console.log('🔍 Sidebar: Closing dropdown');
    setDropdownOpen(false);
  };

  const handleCompanyChange = async (companyId) => {
    console.log('🔍 Sidebar: handleCompanyChange called with companyId:', companyId);
    console.log('🔍 Sidebar: Available companies:', companies);
    
    const selected = companies.find(c => c._id === companyId);
    console.log('🔍 Sidebar: Selected company:', selected);
    
    // Check if selected company exists and has a name
    if (!selected || !selected.name) {
      console.warn('Sidebar: No company selected or company name is missing');
      if (selected) {
        onCompanySelect(selected);
      }
      return;
    }
    
    // Temporary fix: Skip API call for AYUR4LIFE HERBALS INDIA and use basic data
    if (selected.name === 'AYUR4LIFE HERBALS INDIA') {
      console.log('🔍 Sidebar: Using basic company data for AYUR4LIFE HERBALS INDIA (API bypass)');
      onCompanySelect(selected);
      return;
    }

    try {
      // Fetch complete company details
      const idToken = await currentUser.getIdToken(true);
      const apiUrl = `${API_CONFIG.BASE_URL}/api/companies/details-by-name/${encodeURIComponent(selected.name)}`;
      console.log('🔍 Sidebar: API URL:', apiUrl);
      console.log('🔍 Sidebar: Company name being requested:', selected.name);
      
      const response = await axios.get(apiUrl, {
        headers: { 
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 5000
      });

      console.log('🔍 Sidebar: API Response status:', response.status);
      console.log('🔍 Sidebar: API Response data:', response.data);

      if (response.data && response.data.success) {
        console.log('Sidebar: Complete company details fetched:', response.data.company);
        onCompanySelect(response.data.company);
      } else {
        console.warn('Sidebar: API response not successful, using basic company data as fallback');
        onCompanySelect(selected);
      }
    } catch (error) {
      console.error('Sidebar: Error fetching complete company details:', error);
      console.error('Sidebar: Error details:', error.response?.data);
      console.warn('Sidebar: Using basic company data as fallback');
      onCompanySelect(selected);
    }
  };

  const handleClick = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    // Always toggle sidebar
    toggleSidebar();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const greeting = `${getGreeting()}, ${safeUser.name}!`;
  
  const linkVariants = {
    hover: { scale: 1.05, x: 5, transition: { duration: 0.2 } },
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}
      <div className={`sidebar-container ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-menu">
          <div className="greeting">
            <div className="greeting-text">
              {greeting}
              {safeUser.role === 'admin' && <FaCrown className="crown-icon" />}
            </div>
            <div className="greeting-time">
              {new Date().toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })}
            </div>
          </div>

          <div className="sidebar-company-selector">
            <div className="custom-company-dropdown">
              <button className="dropdown-toggle" onClick={toggleDropdown}>
                <img
                  src={getLogoUrl(selectedCompany?.logoUrl, selectedCompany?.name)}
                  alt="Logo"
                  className="dropdown-logo"
                  onError={(e) => {
                    if (e.target.src !== defaultLogo) e.target.src = defaultLogo;
                  }}
                />
                <span>{selectedCompany?.name || 'Select a Company'}</span>
                <i className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="dropdown-options">
                  {loadingCompanies ? (
                    <div className="dropdown-option loading">Loading companies...</div>
                  ) : !companies || companies.length === 0 ? (
                    <div className="dropdown-option empty">No companies available</div>
                  ) : (
                    companies.map((company) => (
                      <div
                        key={company._id}
                        className="dropdown-option"
                        onClick={() => {
                          console.log('🔍 Sidebar: Company clicked:', company.name, 'ID:', company._id);
                          handleCompanyChange(company._id);
                          closeDropdown();
                        }}
                      >
                        <img
                          src={getLogoUrl(company.logoUrl, company.name)}
                          alt={company.name}
                          className="dropdown-logo"
                          onError={(e) => {
                            if (e.target.src !== defaultLogo) e.target.src = defaultLogo;
                          }}
                        />
                        <div className="company-info">
                        <span className="fw-semibold ps-2">{company.name}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {selectedCompany && (
            <motion.div
              className="company-display"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.img
                src={getLogoUrl(selectedCompany.logoUrl, selectedCompany.name)}
                alt={`${selectedCompany.name} Logo`}
                className="company-logo"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                onError={(e) => {
                  if (e.target.src !== defaultLogo) e.target.src = defaultLogo;
                }}
              />
              <span className="company-name">{selectedCompany.name}</span>
            </motion.div>
          )}

          <ul className="options">
            {selectedCompany ? (
              <>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to="/customers" className="sidebar-link" onClick={handleClick}>
                    <FaUsers className="sidebar-icon" />
                    <span>Manage Customers</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/cash-bill?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaFileInvoiceDollar className="sidebar-icon" />
                    <span>Cash Bill</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/credit-bill?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaCreditCard className="sidebar-icon" />
                    <span>Credit Bill</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/credit-note?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaStickyNote className="sidebar-icon" />
                    <span>Credit Note</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/debit-note?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaStickyNote className="sidebar-icon" />
                    <span>Debit Note</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/payment-receipt?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaReceipt className="sidebar-icon" />
                    <span>Payment Receipt</span>
                  </Link>
                </motion.li>
                <motion.li variants={linkVariants} whileHover="hover">
                  <Link to={`/quotation?company=${encodeURIComponent(selectedCompany?.name || '')}`} className="sidebar-link" onClick={handleClick}>
                    <FaFileAlt className="sidebar-icon" />
                    <span>Quotation</span>
                  </Link>
                </motion.li>
              </>
            ) : (
              <motion.li variants={linkVariants} whileHover="hover">
                <div className="sidebar-link disabled">
                  <FaUsers className="sidebar-icon" />
                  <span>Please select a company first</span>
                </div>
              </motion.li>
            )}
            
            <motion.li variants={linkVariants} whileHover="hover" className="logout-item">
              <button
                className="signout-btn"
                onClick={() => {
                  handleClick();
                  window.location.href = '/logout';
                }}
              >
                <FaCrown className="sidebar-icon" />
                <span>Sign Out</span>
              </button>
            </motion.li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Sidebar;